import { useEffect, useRef, type ReactElement } from "react";
import { useLocation } from "react-router-dom";
import { useReporting } from "@/hooks/useReporting";
import { useCMSReports } from "@/hooks/useCMSData";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { normalizeRoute } from "@/hooks/reportRoute";
import {
  setReportDownloadEmitter,
  clearReportDownloadEmitter,
} from "@/pages/reports/components/downloadCsv";
import {
  setReportFilterEmitter,
  clearReportFilterEmitter,
} from "@/pages/reports/components/reportFilterTelemetry";

/**
 * How long a filter burst stays open. Checking five boxes in one dropdown is a
 * single interaction and must report once — useReporting's note on
 * report-filtered is explicit that an event per keystroke is the failure mode
 * here, with the 4.74M-row application-error flood as the worked example.
 */
const FILTER_SETTLE_MS = 1200;

interface CurrentReport {
  name: string;
  route: string;
}

const NO_REPORT: CurrentReport = { name: "", route: "" };

/**
 * Mounts reporting for the life of the app and tells downloadCsv which report
 * the user is looking at.
 *
 * WHY A COMPONENT RATHER THAN A CALL IN App. useReporting owns a useMutation and
 * subscribes to the myProducts query, so calling it from App put that state on
 * the root and re-rendered Router on every reported event. Here those
 * transitions re-render one component that draws nothing.
 *
 * WHY THE REPORT NAME IS RESOLVED HERE. downloadCsv only knows the filename it
 * was handed, which is scoped per school and stamped with the date — so keying
 * downloads on it produced one series per school per day and nothing that
 * joined to report-opened. What is actually wanted is the report the user was
 * on. This component sits inside BrowserRouter, so it can read the route and
 * match it against the CMS report list, which is the same source report-opened
 * uses. No call site has to pass anything, and a report added in the CMS is
 * resolved without a code change.
 */
function ReportTelemetryActive(): null {
  const { reportReportDownloaded, reportReportFiltered } = useReporting();
  const { reports } = useCMSReports();
  const { pathname } = useLocation();

  // Refs, not deps: the emitter is registered once, and re-registering on every
  // navigation would race downloads in flight. Reading through a ref means the
  // registered function always sees the current route and the current reporter.
  const pathRef = useRef(pathname);
  pathRef.current = pathname;
  const reportsRef = useRef(reports);
  reportsRef.current = reports;
  const reporterRef = useRef(reportReportDownloaded);
  reporterRef.current = reportReportDownloaded;
  const filterReporterRef = useRef(reportReportFiltered);
  filterReporterRef.current = reportReportFiltered;

  // Shared by both emitters: whichever report the user is on right now.
  //
  // Longest matching route wins, so a detail page like
  // /reports/ms-usage-report/school/<id> resolves to its parent report rather
  // than to a shorter route that also prefixes it.
  //
  // Normalised on both sides with useReportTitle's own helper: the CMS route
  // field is unvalidated, so "reports/foo" and "/reports/foo/" both occur and a
  // raw startsWith silently matched neither.
  //
  // Falling back to the route keeps the series stable and low-cardinality when
  // the CMS list has not loaded or a report is not in it. The matched CMS
  // route (not the full pathname) is what keys the event, so a detail page
  // counts against its parent report.
  const currentReport = useRef<() => CurrentReport>(() => NO_REPORT);
  currentReport.current = () => {
    const path = pathRef.current;
    const target = normalizeRoute(path);
    const match = (reportsRef.current ?? [])
      .map((r) => ({ r, route: normalizeRoute(r?.route) }))
      .filter(({ route }) => route && target.startsWith(route))
      .sort((a, b) => b.route.length - a.route.length)[0];
    return match
      ? { name: match.r.name, route: match.route }
      : { name: path, route: path };
  };

  useEffect(() => {
    const emit = (filename: string, rowCount: number): void => {
      const { name, route } = currentReport.current();
      void reporterRef.current(name, route, rowCount, filename).catch(() => {});
    };

    setReportDownloadEmitter(emit);
    // Identity-checked, so a teardown that runs after the replacement has
    // already registered does not wipe it. An unconditional
    // setReportDownloadEmitter(null) here caused exactly that.
    return () => clearReportDownloadEmitter(emit);
  }, []);

  useEffect(() => {
    // One burst = one event. Labels are collected in a Set so ticking Schools
    // then Grade inside the window reports both dimensions, once.
    let timer: ReturnType<typeof setTimeout> | null = null;
    let pending = new Set<string>();
    // Captured when the burst OPENS, not when it fires: a coach who filters and
    // immediately navigates away would otherwise have the event attributed to
    // whatever page they landed on.
    let pendingReport = NO_REPORT;

    const flush = (): void => {
      timer = null;
      const labels = [...pending].sort();
      const { name, route } = pendingReport;
      pending = new Set();
      pendingReport = NO_REPORT;
      if (labels.length === 0) return;
      void filterReporterRef.current(name, route, labels).catch(() => {});
    };

    const emit = (label: string): void => {
      if (timer === null) pendingReport = currentReport.current();
      pending.add(label);
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(flush, FILTER_SETTLE_MS);
    };

    setReportFilterEmitter(emit);
    return () => {
      // Dropped rather than flushed. This component unmounts only on logout or
      // app teardown — navigating between reports keeps it mounted — so at most
      // one in-flight burst is lost, and firing a mutation into a tearing-down
      // tree is the worse trade.
      if (timer !== null) clearTimeout(timer);
      clearReportFilterEmitter(emit);
    };
  }, []);

  return null;
}

/**
 * Climbers never reach a report page, so they never call downloadCsv — but
 * mounting the inner component at the app root made every climber session
 * fetch the plan-scoped report index JSON it could never use. Gate on role and
 * the fetch disappears for the large majority of sessions. Split into two
 * components because useCMSReports cannot be called conditionally.
 */
export default function ReportTelemetry(): ReactElement | null {
  const currentUser = useCurrentUser();
  if (!currentUser?.role || currentUser.role === "climber") return null;
  return <ReportTelemetryActive />;
}
